import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { FollowButton } from '../components/FollowButton';
import { MessageButton } from '../components/MessageButton';
import BookingModal from './BookingModal';
import ServiceCard from './ServiceCard';
import { API } from '../config/api';
import {
  Mic2, MapPin, Share2, Clock, ExternalLink,
  Verified, FileText, Hash, Play, Users,
  Loader2, Calendar, Headphones, DollarSign
} from 'lucide-react';

export default function PodcasterProfileView({ profile, roleProfile, isOwnProfile, followerCount, followingCount, isFollowing, onFollowChange }) {
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState([]);
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('episodes');
  const [loadingContent, setLoadingContent] = useState(true);
  const [showBooking, setShowBooking] = useState(false);

  const rp = roleProfile || {};
  const displayName = rp.display_name || rp.stage_name || profile.name;
  const showName = rp.show_name || profile.show_name;
  const bio = rp.bio || profile.bio;
  const topics = rp.topics || profile.topics || [];
  const genres = rp.genres || profile.genres || [];
  const schedule = rp.schedule || profile.schedule;
  const priceRange = rp.price_range || rp.rate_range || profile.rate_range;

  useEffect(() => { fetchContent(); }, [profile.user_id]);

  const fetchContent = async () => {
    setLoadingContent(true);
    try {
      const [epRes, svcRes, postRes] = await Promise.all([
        fetch(`${API}/creative-episodes/user/${profile.user_id}`),
        fetch(`${API}/services/user/${profile.user_id}`),
        fetch(`${API}/posts?user_id=${profile.user_id}&limit=20`),
      ]);
      if (epRes.ok) { const d = await epRes.json(); setEpisodes(d.episodes || []); }
      if (svcRes.ok) { const d = await svcRes.json(); setServices(d.services || []); }
      if (postRes.ok) { const d = await postRes.json(); setPosts(Array.isArray(d) ? d : d.posts || []); }
    } catch (e) { console.error('Content fetch error:', e); }
    setLoadingContent(false);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/u/${profile.handle}`;
    try { await navigator.clipboard.writeText(url); toast.success('Profile link copied!'); }
    catch { toast.error('Could not copy link'); }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return null;
    const s = parseInt(seconds);
    if (s <= 0) return null;
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen" data-testid="podcaster-profile">
      {/* Banner */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        {rp.banner_url || profile.banner_url ? (
          <img src={rp.banner_url || profile.banner_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-green-900/40 via-zinc-900 to-emerald-800/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
      </div>

      {/* Profile Header */}
      <div className="max-w-5xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          {/* Avatar */}
          <div className="w-28 h-28 rounded-2xl border-4 border-background overflow-hidden bg-muted flex-shrink-0 shadow-xl">
            {(rp.avatar_url || profile.avatar_url || profile.picture) ? (
              <img src={rp.avatar_url || profile.avatar_url || profile.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Mic2 className="w-10 h-10 text-green-500/50" /></div>
            )}
          </div>

          {/* Info + Actions */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-bold">{displayName}</h1>
              {(rp.is_verified || profile.is_verified) && <Verified className="w-5 h-5 text-green-400" />}
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Podcaster</Badge>
            </div>
            <p className="text-muted-foreground text-sm">{profile.handle ? `@${profile.handle}` : ""}</p>

            {/* Show Name */}
            {showName && (
              <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
                <Headphones className="w-3 h-3" />{showName}
              </p>
            )}

            {/* Topics */}
            {topics.length > 0 && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {topics.map(t => (
                  <Badge key={t} variant="outline" className="bg-green-500/10 border-green-500/20 text-green-300 text-xs">
                    <Hash className="w-3 h-3 mr-1" />{t}
                  </Badge>
                ))}
              </div>
            )}

            {/* Location + Schedule + Rate */}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {(rp.neighborhood || profile.neighborhood || profile.city || profile.location) && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{rp.neighborhood || profile.neighborhood || profile.city || profile.location}</span>
              )}
              {schedule && (
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{schedule}</span>
              )}
              {priceRange && (
                <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{priceRange}</span>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-5 mt-3">
              <div className="text-center"><p className="font-bold text-lg">{followerCount}</p><p className="text-xs text-muted-foreground">Followers</p></div>
              <div className="text-center"><p className="font-bold text-lg">{followingCount}</p><p className="text-xs text-muted-foreground">Following</p></div>
              <div className="text-center"><p className="font-bold text-lg">{episodes.length}</p><p className="text-xs text-muted-foreground">Episodes</p></div>
            </div>

            {/* Action Buttons */}
            {!isOwnProfile && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <FollowButton targetUserId={profile.user_id} initialIsFollowing={isFollowing} onFollowChange={onFollowChange} />
                <MessageButton targetUserId={profile.user_id} targetName={displayName} variant="outline" size="default" showText />
                <Button onClick={() => setShowBooking(true)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="book-podcaster-btn">
                  <Mic2 className="w-4 h-4 mr-2" />Book Podcaster
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare} data-testid="share-podcaster-btn"><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
            {isOwnProfile && (
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" onClick={() => navigate('/dashboard/podcaster')} data-testid="edit-podcaster-profile-btn">
                  <Mic2 className="w-4 h-4 mr-2" />Manage Profile
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare}><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {bio && <p className="mt-5 text-sm text-muted-foreground max-w-2xl leading-relaxed">{bio}</p>}

        {/* Genres */}
        {genres.length > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {genres.map(g => <Badge key={g} variant="secondary" className="text-xs">{g}</Badge>)}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="w-full justify-start bg-muted/30 p-1 mb-6 overflow-x-auto">
            <TabsTrigger value="episodes" className="flex items-center gap-2" data-testid="pod-tab-episodes">
              <Mic2 className="w-4 h-4" />Episodes{episodes.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{episodes.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2" data-testid="pod-tab-services">
              <Calendar className="w-4 h-4" />Services{services.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{services.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="posts" className="flex items-center gap-2" data-testid="pod-tab-posts">
              <FileText className="w-4 h-4" />Posts{posts.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{posts.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Episodes Tab */}
          <TabsContent value="episodes">
            {loadingContent ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : episodes.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Mic2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No episodes yet</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Add your episodes from your dashboard.' : 'This podcaster hasn\'t uploaded any episodes yet.'}</p>
                {isOwnProfile && <Button className="mt-4" onClick={() => navigate('/dashboard/podcaster')}>Add Episodes</Button>}
              </Card>
            ) : (
              <div className="space-y-3">
                {episodes.map((ep) => (
                  <Card key={ep.episode_id} className="glass-card border-border p-4 hover:bg-muted/20 transition-colors" data-testid={`episode-card-${ep.episode_id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {ep.cover_art_url ? (
                          <img src={ep.cover_art_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Mic2 className="w-6 h-6 text-green-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium">{ep.title}</h4>
                          {ep.season && <Badge variant="outline" className="text-xs bg-green-500/10 border-green-500/20 text-green-300">S{ep.season}</Badge>}
                          {ep.episode_number && <Badge variant="outline" className="text-xs bg-green-500/10 border-green-500/20 text-green-300">E{ep.episode_number}</Badge>}
                        </div>
                        {ep.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{ep.description}</p>}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {ep.duration && (
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(ep.duration)}</span>
                          )}
                          {ep.plays_count > 0 && (
                            <span className="flex items-center gap-1"><Play className="w-3 h-3" />{ep.plays_count} plays</span>
                          )}
                          {ep.topics?.length > 0 && (
                            <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{ep.topics.slice(0, 3).join(', ')}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {ep.external_url && (
                          <a href={ep.external_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon"><ExternalLink className="w-4 h-4 text-green-400" /></Button>
                          </a>
                        )}
                        {ep.audio_url && (
                          <a href={ep.audio_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon"><Play className="w-4 h-4 text-green-400" /></Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            {services.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No services listed</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Add services from your dashboard.' : 'No services listed yet.'}</p>
                {isOwnProfile && <Button className="mt-4" onClick={() => navigate('/dashboard/podcaster')}>Add Services</Button>}
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {services.map(svc => <ServiceCard key={svc.service_id} service={svc} onBook={!isOwnProfile ? () => setShowBooking(true) : null} />)}
              </div>
            )}
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts">
            {posts.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No posts yet</h3>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map(p => (
                  <Card key={p.post_id} className="glass-card border-border p-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate(`/post/${p.post_id}`)}>
                    <p className="text-sm">{p.text || p.content}</p>
                    {p.media_url && <img src={p.media_url} alt="" className="mt-3 rounded-lg max-h-64 object-cover" />}
                    <p className="text-xs text-muted-foreground mt-2">{new Date(p.created_at).toLocaleDateString()}</p>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Modal */}
      {showBooking && (
        <BookingModal
          targetId={profile.user_id}
          targetName={displayName}
          targetRole="podcaster"
          services={services}
          onClose={() => setShowBooking(false)}
        />
      )}
    </div>
  );
}
